// «Деньги под риском» — dashboard digest of the attention queue.
//
// Every row is one open deal of the workspace, ranked server-side by
// `amount × (1 − score / 100)`. The widget deliberately re-derives nothing:
// the score and the money come from the API, so the number a manager reads
// here is the same number the deal card shows. Clicking a row opens the deal
// where the signals can be acted on.
import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { AlertTriangle, ArrowUpRight, RefreshCw } from 'lucide-react';
import { Typography } from 'antd';

import {
  ATTENTION_QUEUE_WIDGET_LIMIT,
  useAttentionQueue,
} from '../../features/deals/hooks/useAttentionQueue.js';
import { scoreToneColor } from '../../features/deals/lib/score-tone.js';
import { formatMoney } from '../../shared/format/index.js';
import { getEnumLabel } from '../../shared/format/enum-labels.js';
import { nbaQueueReasonText, type NbaLabelI18n } from '../../shared/format/nba-labels.js';
import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';

const { Text } = Typography;

/** Reason chips per row: the strongest two explain a row, the rest are noise. */
const MAX_REASON_CHIPS = 2;

export interface AttentionQueueWidgetProps {
  className?: string;
  testId?: string;
  /** Rows to show; the API page is requested with the same size. */
  limit?: number;
  onOpenDeal?: (dealId: string) => void;
  onViewAll?: () => void;
}

export function AttentionQueueWidget({
  className,
  testId,
  limit = ATTENTION_QUEUE_WIDGET_LIMIT,
  onOpenDeal,
  onViewAll,
}: AttentionQueueWidgetProps): ReactElement {
  const { t, tc } = useTranslation();
  const labels: NbaLabelI18n = { t, tc };
  const { data, isLoading, isError, refetch } = useAttentionQueue(limit);
  const rows = data ?? [];
  const canOpen = typeof onOpenDeal === 'function';

  const widgetContainerProps = {
    title: t('crm.features.deals.nextBestAction.queue.title'),
    icon: AlertTriangle,
    iconColor: 'var(--ant-color-warning, #d97706)',
    minHeight: 220,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
    headerRight: (
      <button
        type="button"
        {...(onViewAll ? { onClick: onViewAll } : {})}
        disabled={!onViewAll}
        className="flex items-center gap-1 font-medium"
        style={{
          fontSize: 11,
          color: onViewAll
            ? 'var(--ant-color-primary-text, #b45309)'
            : 'var(--ant-color-text-tertiary, #6d6963)',
          cursor: onViewAll ? 'pointer' : 'default',
        }}
      >
        {t('crm.features.deals.nextBestAction.queue.open')} <ArrowUpRight size={10} />
      </button>
    ),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  if (isLoading) {
    return (
      <WidgetContainer {...widgetContainerProps}>
        <div
          className="animate-pulse flex flex-col gap-1.5 p-3.5"
          data-testid="attention-queue-loading"
        >
          {[1, 2, 3, 4].map((index) => (
            <div
              key={index}
              className="crm-radius-s h-11"
              style={{ background: 'var(--g-fill, #f5f1eb)' }}
            />
          ))}
        </div>
      </WidgetContainer>
    );
  }

  if (isError) {
    return (
      <WidgetContainer {...widgetContainerProps}>
        <div
          className="flex flex-col items-center justify-center gap-2 text-center"
          style={{ padding: '24px 16px' }}
          data-testid="attention-queue-error"
        >
          <span style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>
            {t('crm.features.deals.nextBestAction.queue.error')}
          </span>
          <button
            type="button"
            onClick={() => void refetch()}
            className="flex items-center gap-1 border-0 bg-transparent text-xs font-medium"
            style={{ color: 'var(--ant-color-primary)', cursor: 'pointer' }}
          >
            <RefreshCw size={12} />
            {t('crm.features.deals.nextBestAction.queue.retry')}
          </button>
        </div>
      </WidgetContainer>
    );
  }

  if (rows.length === 0) {
    return (
      <WidgetContainer {...widgetContainerProps}>
        <div
          className="flex flex-col items-center justify-center gap-1.5 text-center"
          style={{ padding: '24px 16px' }}
          data-testid="attention-queue-empty"
        >
          <AlertTriangle size={16} style={{ color: 'var(--ant-color-text-tertiary)' }} />
          <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
            {t('crm.features.deals.nextBestAction.queue.empty')}
          </span>
          <span style={{ fontSize: 11, color: 'var(--ant-color-text-quaternary, #a8a29e)' }}>
            {t('crm.features.deals.nextBestAction.queue.empty_hint')}
          </span>
        </div>
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer {...widgetContainerProps}>
      <ul className="m-0 list-none p-0">
        {rows.map((row) => {
          const reasonCodes = row.reasonCodes.slice(0, MAX_REASON_CHIPS);
          const hiddenReasons = row.reasonCodes.length - reasonCodes.length;

          return (
            <li key={row.dealId}>
              <button
                type="button"
                {...(canOpen ? { onClick: () => onOpenDeal(row.dealId) } : {})}
                disabled={!canOpen}
                aria-label={t('crm.features.deals.nextBestAction.queue.row_aria', {
                  title: row.title,
                  risk: formatMoney(row.riskAmount, 'RUB'),
                })}
                className="crm-hover-fill flex w-full items-center gap-3 border-0 bg-transparent text-left transition-colors"
                style={{
                  cursor: canOpen ? 'pointer' : 'default',
                  padding: '6px 14px',
                  minHeight: 48,
                  borderBottom: '1px solid var(--ant-color-border-secondary)',
                }}
                data-testid={`attention-queue-row-${row.dealId}`}
              >
                <div className="min-w-0 flex-1">
                  <Text
                    strong
                    className="truncate block"
                    style={{ fontSize: 12.5, color: 'var(--ant-color-text)', lineHeight: 1.3 }}
                  >
                    {row.title}
                  </Text>
                  <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                    <span style={{ fontSize: 11, color: 'var(--ant-color-text-tertiary)' }}>
                      {getEnumLabel('dealStage', row.stage, t)}
                    </span>
                    <span
                      aria-label={t('crm.features.deals.nextBestAction.queue.score')}
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: scoreToneColor(row.score),
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {row.score}%
                    </span>
                    {reasonCodes.map((code) => (
                      <span
                        key={code}
                        className="crm-radius-s"
                        style={{
                          fontSize: 10,
                          padding: '0 5px',
                          color: 'var(--ant-color-text-secondary)',
                          background: 'var(--g-fill, #f5f1eb)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {nbaQueueReasonText(labels, code)}
                      </span>
                    ))}
                    {hiddenReasons > 0 ? (
                      <span style={{ fontSize: 10, color: 'var(--ant-color-text-tertiary)' }}>
                        +{hiddenReasons}
                      </span>
                    ) : null}
                  </span>
                </div>
                <span className="shrink-0 text-right">
                  <span
                    className="block"
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'var(--ant-color-error-text, #b91c1c)',
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: 1.3,
                    }}
                  >
                    {formatMoney(row.riskAmount, 'RUB')}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--ant-color-text-tertiary)' }}>
                    {t('crm.features.deals.nextBestAction.queue.amount_of', {
                      amount: formatMoney(row.amount, 'RUB'),
                    })}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </WidgetContainer>
  );
}
